/**
 * Small data-fetching hooks.
 *
 * No query library: this app has a handful of endpoints, all of which are either
 * "fetch once on mount" or "fetch the next page". Adding a cache layer would be more
 * code than it saves, and the catalog is far too large to cache client-side anyway.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CatalogItemDto, Paginated } from '@filmodasi/shared';
import { api, ApiRequestError } from './client';

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

export function useApi<T>(path: string | null, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!path) {
      setData(null);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    api<T>(path, { signal: controller.signal })
      .then(setData)
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiRequestError ? err.message : 'Yüklenemedi');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps]);

  return { data, error, loading, reload: useCallback(() => setNonce((n) => n + 1), []) };
}

const PAGE_SIZE = 48;

export interface PagedItems {
  items: CatalogItemDto[];
  total: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
}

/**
 * Infinite grid paging.
 *
 * A live library can hold 20,000 channels, so there is no "load everything" path — the
 * grid appends a page at a time and the total comes from the server.
 */
export function usePagedCatalog(basePath: string, query: Record<string, string>): PagedItems {
  const [items, setItems] = useState<CatalogItemDto[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const key = JSON.stringify(query);
  const seen = useRef(new Set<string>());

  // A changed filter is a different list, not more of the same one.
  useEffect(() => {
    setItems([]);
    setTotal(0);
    setPage(1);
    seen.current = new Set();
  }, [basePath, key]);

  useEffect(() => {
    // An empty path means "not asking right now" — the same contract `useApi` gives a
    // null path. The library screen needs it: while it is showing discovery rows there
    // is no grid to page, and firing a request at `?page=1` would be a 404 the user
    // would see as a broken screen.
    if (!basePath) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const params = new URLSearchParams({ ...query, page: String(page), limit: String(PAGE_SIZE) });
    setLoading(true);
    setError(null);

    api<Paginated<CatalogItemDto>>(`${basePath}?${params}`, { signal: controller.signal })
      .then((res) => {
        setTotal(res.total);
        setItems((prev) => {
          // Paging while the catalog is being synced can repeat an item; de-duplicating
          // here keeps React keys unique instead of throwing.
          const fresh = res.items.filter((i) => !seen.current.has(i.id));
          for (const i of fresh) seen.current.add(i.id);
          return page === 1 ? res.items : [...prev, ...fresh];
        });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiRequestError ? err.message : 'Yüklenemedi');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
    // `key`, not `query`: the caller builds the filter object inline, so it is a new
    // identity every render and depending on it would refetch the page continuously.
    // `key` is its serialisation, which changes exactly when the filter does.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands in for `query`
  }, [basePath, key, page]);

  return {
    items,
    total,
    loading,
    error,
    hasMore: items.length < total,
    loadMore: useCallback(() => setPage((p) => p + 1), []),
  };
}
